import useFormatGraphData from '@/hooks/charts/useFormatGraphData';
//hooks
import useFormatNumber, { percentFormat } from '@/hooks/format/useFormatNumber';
import { useFormatTimeIndicator } from '@/hooks/format/useFormatTimeIndicator';
import useUser from '@/hooks/swr/useUser';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

import { Trans } from '@/components/tools/translation';

const TooltipCustom = ({
    active,
    payload,
    valueFormat = {},
    timeFormat = null,
    tooltipTranslation = null,
    tooltipValueTranslation = null,
    userNumbersFormat = null,
    data = {},
    activeFilter
}) => {
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { t, lang } = useTranslation('commons');
    const { formatPercentValue, formatEvolutionPercentValue } = useFormatGraphData();
    const { formatNumber } = useFormatNumber(user?.settings?.numbers_format ?? 'comma');
    const { formatDateTimeNoTz } = useFormatDateTime(user?.settings?.timezone);
    const { formatHour } = useFormatTimeIndicator();

    const getTranslationValues = () => {
        const values = { ...(tooltipTranslation?.customValues || {}) };

        tooltipTranslation?.values?.forEach((entryFormat) => {
            const entry = entryFormat.entry || entryFormat;
            const format = entryFormat.format || entryFormat;

            switch (format) {
                case 'date':
                    if (
                        !payload[0].payload[entry].includes('h') &&
                        !payload[0].payload[entry].includes('pm') &&
                        !payload[0].payload[entry].includes('am')
                    ) {
                        values[entry] = formatDateTimeNoTz(
                            payload[0].payload[entry],
                            userNumbersFormat?.date?.format || user?.settings?.date_format
                        );
                    } else {
                        values[entry] = payload[0].payload[entry];
                    }

                    break;

                case 'day':
                    if (data.id === 'posts_density') {
                        if (payload[0].payload[entry]) {
                            values['postDensity'] = t(`days.${payload[0].payload[entry]}`);
                        }
                    } else {
                        values[entry] = t(`days.${payload[0].payload[entry]}`);
                    }
                    break;

                case 'date_hour':
                    if (payload[0].payload[entry]) {
                        values['postDensity'] = formatHour(payload[0].payload[entry]);
                    }

                    break;

                case 'gender':
                    values[entry] = t(`analytics:${payload[0].payload[entry]}`);

                    break;
                case 'percentage':
                    if (
                        data.id === 'languages_distribution' ||
                        data.id === 'ages_genders_distribution-pie' ||
                        data.id === 'reached_audience_demographics_distribution-pie'
                    ) {
                        values[entry] = formatNumber(payload[0].payload.percentage, percentFormat);
                    } else {
                        values[entry] = formatPercentValue(payload[0].value, data.graphData);
                    }
                    break;

                case 'percent_value':
                    values[entry] = `${payload[0].value}%`;
                    break;

                case 'percentage_evolution':
                    values[entry] = formatEvolutionPercentValue(
                        payload[0].value,
                        data.graphData[0]?.avg
                    );
                    break;

                case 'name':
                    values[entry] = t(
                        `analytics:chart.post_engagements.${payload[0].payload.type}`
                    );
                    break;

                case 'generic_name':
                    values['name'] = t(`analytics:${data?.name[payload[0].payload.type]}`);
                    break;

                case 'time':
                    values[entry] = timeFormat(payload[0].payload[entry]);
                    break;

                case 'value':
                    // Check if value is null
                    if (
                        payload[0].payload.value ||
                        payload[0].value === 0 ||
                        payload[0].payload.value === 0
                    ) {
                        // If custom translation contains a percent value
                        if (data.isPercentValue) {
                            values[entry] = formatNumber(payload[0].payload.value, {
                                ...percentFormat,
                                maximumFractionDigits: 5
                            });
                            // If the formatted value depends on activeFilter
                        } else if (data.valueFormat && typeof data.valueFormat === 'object') {
                            values[entry] = formatNumber(
                                payload[0].payload.value,
                                data.valueFormat[activeFilter]
                            );
                        } else {
                            values[entry] = formatNumber(payload[0].payload.value, valueFormat);
                        }
                    } else {
                        values[entry] = 'N/A';
                    }
                    break;
                case 'previous_value':
                    // Check if value is null
                    if (
                        payload?.[1]?.payload?.previous_value ||
                        payload?.[1]?.value === 0 ||
                        payload?.[1]?.payload?.previous_value === 0
                    ) {
                        // If custom translation contains a percent value
                        if (data.isPercentValue) {
                            values[entry] = formatNumber(payload[1].value, {
                                ...percentFormat,
                                maximumFractionDigits: 5
                            });
                            // If the formatted value depends on activeFilter
                        } else if (data.valueFormat && typeof data.valueFormat === 'object') {
                            values[entry] = formatNumber(
                                payload?.[1]?.value,
                                data.valueFormat[activeFilter]
                            );
                        } else {
                            values[entry] = formatNumber(payload?.[1]?.value, valueFormat);
                        }
                    } else {
                        values[entry] = 'N/A';
                    }
                    break;

                default:
                    values[entry] = payload?.[0]?.payload?.[entry];
                    break;
            }
        });

        return values;
    };

    const getTranslationToken = () => {
        if (tooltipTranslation.filtersToken) {
            return `analytics:${tooltipTranslation.filtersToken[activeFilter]}`;
        }
        return `analytics:${tooltipTranslation.token}`;
    };

    const displayTimeEntry = (timeEntry, payload) => {
        switch (timeEntry) {
            case 'date':
                return formatDateTimeNoTz(
                    payload[timeEntry],
                    userNumbersFormat?.date?.format || user?.settings?.date_format
                );

            case 'time':
                return timeFormat(payload[timeEntry]);

            default:
                return t(`analytics:${data.tickFormatterTranslation}.${payload[timeEntry]}`);
        }
    };

    const displayTypeEntry = (typeEntry, payload) => {
        switch (typeEntry) {
            case 'reaction':
                return t(`analytics:chart.post_reactions_facebook.${payload[typeEntry]}`);
            default:
                return t(`analytics:chart.post_engagements.${payload[typeEntry]}`);
        }
    };

    const displayTotal = (payload) => {
        if (payload[0].payload.total) {
            switch (data.id) {
                case 'page_cta_clicks_history':
                case 'page_views_history':
                case 'page_likes_history':
                case 'post_engagements_history':
                case 'gained_lost_fans_history':
                case 'gained_lost_followers_history':
                    return (
                        <span>
                            {t(`analytics:${data.name['total']}`)}
                            {lang === 'fr' ? ' : ' : ': '}
                            {formatNumber(payload[0].payload.total, {
                                ...valueFormat,
                                signDisplay: 'always'
                            })}
                        </span>
                    );
                case 'average_reach_per_post_type_distribution':
                    return (
                        <span>
                            {t(`analytics:${data.name['total']}`)}
                            {lang === 'fr' ? ' : ' : ': '}
                            {formatNumber(payload[0].payload.total, {
                                ...valueFormat,
                                signDisplay: 'never'
                            })}
                        </span>
                    );
                default:
                    return;
            }
        }
    };

    const includesTimeEntryPattern = (payload) => {
        const timeEntryPatterns = ['time', 'date', 'month'];

        return payload.find((entry) => timeEntryPatterns.includes(entry));
    };

    const includesTypeEntryPattern = (payload) => {
        const typeEntryPatterns = ['type', 'reaction'];

        return payload.find((entry) => typeEntryPatterns.includes(entry));
    };

    if (active && payload && payload.length) {
        const timeEntry = includesTimeEntryPattern(Object.keys(payload[0].payload));
        const typeEntry = includesTypeEntryPattern(Object.keys(payload[0].payload));

        return (
            <div
                className="relative rounded-md bg-icoBlack p-2 text-xs text-white dark:bg-white dark:text-icoDarkMode-deepBlack"
                data-testid="tooltip-custom">
                {data.id === 'ages_genders_distribution' ||
                data.id === 'reached_audience_demographics_distribution' ? (
                    <>
                        <span className="block">{payload[0].payload.age}</span>
                        {payload.map((entry, index) => {
                            if (
                                entry.dataKey !== 'percentage_female' &&
                                entry.dataKey !== 'percentage_male'
                            ) {
                                const percentageValue =
                                    payload[0].payload[`percentage_${entry.dataKey}`];

                                if (percentageValue) {
                                    return (
                                        <span key={index} className="block">
                                            <span>{`${entry.name}${
                                                lang === 'fr' ? ' : ' : ': '
                                            }`}</span>
                                            <Trans
                                                i18nKey={`analytics:${tooltipValueTranslation}`}
                                                values={{
                                                    value: formatNumber(entry.value, valueFormat),
                                                    percentage: formatNumber(percentageValue, {
                                                        ...percentFormat,
                                                        maximumFractionDigits: 5
                                                    })
                                                }}
                                            />
                                        </span>
                                    );
                                }
                            }
                        })}
                    </>
                ) : !tooltipTranslation ? (
                    <>
                        {timeEntry && (
                            <span className="block">
                                {displayTimeEntry(timeEntry, payload[0].payload)}
                            </span>
                        )}
                        {typeEntry && (
                            <span className="block">
                                {displayTypeEntry(typeEntry, payload[0].payload)}
                            </span>
                        )}
                        {payload.map((entry, index) => {
                            const payloadKeys = Object.keys(
                                entry.payload?.payload ?? entry.payload
                            );
                            const isEstimatedDuplicatedValue =
                                entry.payload.value === entry.payload?.estimated &&
                                entry.dataKey === 'estimated';

                            if (!isEstimatedDuplicatedValue) {
                                let existingDataValuesProperties = [];
                                if (data.dataValuesProperties) {
                                    data.dataValuesProperties.forEach((dataValueProperty) => {
                                        if (
                                            Object.keys(data.graphData[0]).includes(
                                                dataValueProperty
                                            )
                                        ) {
                                            existingDataValuesProperties.push(dataValueProperty);
                                        }
                                    });
                                }

                                return (
                                    <span key={index} className="block" data-testid={entry.name}>
                                        {(existingDataValuesProperties.length > 1 ||
                                            (!payloadKeys.includes('avg') &&
                                                (!typeEntry ||
                                                    data.id ===
                                                        'average_stories_reach_impressions_per_story_type'))) &&
                                            `${
                                                entry.dataKey === 'estimated'
                                                    ? t('analytics:estimated')
                                                    : entry.name
                                            }${lang === 'fr' ? ' : ' : ': '}`}
                                        {entry.value || entry.value === 0 ? (
                                            tooltipValueTranslation ? (
                                                <Trans
                                                    i18nKey={`analytics:${tooltipValueTranslation}`}
                                                    values={{
                                                        value: formatNumber(
                                                            entry.value,
                                                            valueFormat
                                                        )
                                                    }}
                                                />
                                            ) : (
                                                formatNumber(entry.value, valueFormat)
                                            )
                                        ) : (
                                            'N/A'
                                        )}
                                    </span>
                                );
                            }
                        })}
                        {displayTotal(payload)}
                    </>
                ) : (
                    <Trans
                        i18nKey={getTranslationToken()}
                        values={getTranslationValues()}
                        components={{ br: <br /> }}
                    />
                )}
                {data.adsDataToken && (payload[0].payload.organic || payload[0].payload.paid) && (
                    <p>
                        <Trans
                            i18nKey={`analytics:${data.adsDataToken}`}
                            values={{
                                organic: payload[0].payload.organic ?? 'N/A',
                                paid: payload[0].payload.paid ?? 'N/A'
                            }}
                        />
                    </p>
                )}
            </div>
        );
    } else {
        return null;
    }
};

TooltipCustom.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    valueFormat: PropTypes.object,
    timeFormat: PropTypes.func,
    tooltipTranslation: PropTypes.object,
    tooltipValueTranslation: PropTypes.string,
    activeFilter: PropTypes.string,
    data: PropTypes.object
};

export default TooltipCustom;
